import React, { useState, useEffect, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { Grid, Cell } from 'formlabs-web-components/Grid';
import { Header } from 'formlabs-web-components/Type';
import { Button } from 'formlabs-web-components/Link';
import TextPlugin from '../../../../common/components/cms/TextPlugin';
import { analytics } from '../../../../common/utils/index';

import { DisplayComponent } from 'formlabs-web-components/BaseComponents';

import Material from './Material';
import './MaterialSelector.module.css';

const MaterialSelector = DisplayComponent(
  ({ materials, questions, recommendations }) => {
    const { t } = useTranslation();
    const [filters, setFilters] = useState([]);

    // build list of question tags for display / breadcrumb navigation
    const [breadcrumbs, setBreadcrumbs] = useState([]);

    // build list of question tags setting questions
    const [questionstags, setQuestionstags] = useState(['start']);

    const filteredMaterials = materials.filter(material =>
      filters.every(v => material.tags.includes(v))
    );

    const recommendationSummary = recommendations.filter(recommendation =>
      filters.every(v => recommendation.tags.includes(v))
    );

    const recMaterials = filteredMaterials
      .map(material => `${material.name}`)
      .join(', ')
      .replace(/, ([^,]*)$/, ' and $1');

    const [currentQuestionTag, setCurrentQuestionTag] = useState('intro');

    const currentQuestion = questions.filter(
      question => currentQuestionTag === `${question.question_tag}`
    );

    const end =
      questions.filter(
        question => currentQuestionTag === `${question.question_tag}`
      ).length < 1;

    useEffect(
      () => {
        if (end) {
          analytics.track('Material Selector Tool Final Step', {
            recommendation: recMaterials
          });
        }
      },
      [end]
    );

    useEffect(
      () => {
        document.body.scrollTop = 0; // For Safari
        document.documentElement.scrollTop = 0;
        // Add breadcrumbs when question changes
        if (!end) {
          setBreadcrumbs(breadcrumbs.concat(currentQuestion[0].breadcrumb));
        }
      },
      [currentQuestionTag]
    );

    // On tag click, build/update filter list, update active question, change breadcrumbs if breadcrumb nav clicked
    const updateFilters = (tag, crumbs) => () => {
      let changeBreadcrumbs = breadcrumbs;
      let removeFilters = filters;
      let removeQuestions = questionstags;
      let updatetag = tag;

      if (tag === 'nav') {
        if (crumbs === 0) {
          setFilters([]);
          setBreadcrumbs([]);
          setQuestionstags(['start']);
          updatetag = 'intro';
        } else {
          updatetag = questionstags[crumbs - 1];

          changeBreadcrumbs.length = crumbs;
          setBreadcrumbs(changeBreadcrumbs);

          removeFilters.length = crumbs - 1;
          setFilters(removeFilters);

          removeQuestions.length = crumbs;
          setQuestionstags(removeQuestions);
        }
      } else if (!(tag === 'start') | 'nav') {
        let answeredQuestion = questionstags[questionstags.length - 1];

        analytics.track('Material Selector Tool Continue', {
          question: answeredQuestion,
          answer: tag
        });

        setFilters(filters.concat(tag));

        setQuestionstags(questionstags.concat(tag));
      }
      setCurrentQuestionTag(updatetag);
    };

    // remove: add class before removing, add initial class for all materials to load
    let materialList;

    materialList = (filters.length ? filteredMaterials : materials)
      .sort(
        (a, b) =>
          // sorting by industry name then material name (materials without industries are moved to the start)
          !b.industryName - !a.industryName ||
          a.industryName.localeCompare(b.industryName) ||
          a.name.localeCompare(b.name)
      )
      .map(material => (
        <Material
          key={`${material.name}${material.industryName}`}
          {...material}
        />
      ));

    const questionList = currentQuestion.map(question => (
      <div key={question.breadcrumb}>
        <Header level={2} displayAs={4}>
          {question.question_text}
        </Header>
        {question.helper_text && <p>{question.helper_text}</p>}
        <ul styleName="tags">
          {question.responses.map(response => {
            const intro = response.tag === 'start';
            return (
              <li
                styleName={classNames('tag', {
                  start: intro
                })}
                key={response.tag}
              >
                <Button
                  style={{ display: 'block' }}
                  // if intro make blue
                  color={intro ? 'dark' : 'inherit-color'}
                  onClick={updateFilters(response.tag)}
                >
                  <span styleName={!intro && 'response'}>
                    {response.response_text}
                  </span>{' '}
                  <i className="fl-arrow-right" />
                </Button>
              </li>
            );
          })}
        </ul>
      </div>
    ));

    return (
      <div styleName="materialSelector">
        <Grid direction="x" hasContainer gutters="margin">
          <Cell smallSize={24} mediumSize={11}>
            <div styleName="sticky">
              <Fragment>
                <ul styleName="breadcrumbs">
                  {breadcrumbs.map((breadcrumb, index, arr) => (
                    <li key={breadcrumb}>
                      <Button
                        color="inherit-color"
                        onClick={updateFilters('nav', index)}
                        styleName={
                          arr.length === index + 1 && !end ? 'disabled' : 'blue'
                        }
                      >
                        {breadcrumb}
                      </Button>
                    </li>
                  ))}
                  {end && <li>{t('Recommendation')}</li>}
                </ul>
              </Fragment>
              {questionList}
              {end === true && (
                <div>
                  <Header level={2} displayAs={4}>
                    {t('We recommend {{materials}} for your next print.', {
                      materials: recMaterials
                    })}
                  </Header>

                  <TextPlugin body={recommendationSummary[0].recommendation} />

                  <TextPlugin body={recommendationSummary[0].data} />

                  {filteredMaterials.map(material => (
                    <Button
                      styleName="learn"
                      url={material.link}
                      arrow="right-caret"
                      color="inherit-color"
                      key={material.name}
                    >
                      {t('Buy {{material}} {{type}} Now', {
                        material: material.name,
                        type: material.materialType
                      })}
                    </Button>
                  ))}
                </div>
              )}
            </div>
          </Cell>
          <Cell smallSize={24} mediumSize={12} largeOffset={1}>
            <ul styleName="materials">{materialList}</ul>
          </Cell>
        </Grid>
      </div>
    );
  }
);

export default MaterialSelector;
